import { useMemo } from "react";
import { DECADES, GENRES, MIN_POOL_SIZE, type Decade, type Genre, type Track } from "../../shared/domain.ts";
import { tracksMatching, type Filter } from "../game/pools.ts";
import { DECADE_HUES, decadeLabel, GENRE_VISUALS, genreLabel } from "./labels.ts";

/**
 * The player's own Filter — any number of genres, any number of eras.
 *
 * Nothing is hidden here. The old chip list dropped every option that would
 * take the Pool under the floor, so options vanished and the row reflowed under
 * the player's finger with no explanation. Multi-select makes most thin
 * combinations reachable anyway, and for the ones that stay thin the count says
 * so and Start refuses — the floor is enforced, just out loud.
 *
 * Each choice is a tile with its own colour, glyph and song count rather than a
 * grey pill of text: nine identical pills is the least scannable way to offer a
 * choice between nine things, and the count answers "is this worth picking?"
 * before the player has to try it.
 */
export function CustomMix({
  tracks,
  filter,
  onChange,
  onStart,
}: {
  tracks: Track[];
  filter: Filter;
  onChange: (next: Filter) => void;
  onStart: () => void;
}) {
  // Memoised on the filter, not recomputed per render: this is a full Catalog
  // pass, and the component re-renders on every tile tap.
  const size = useMemo(() => tracksMatching(tracks, filter).length, [tracks, filter]);
  const playable = size >= MIN_POOL_SIZE;

  /**
   * Counts per genre and era, in one pass over the Catalog rather than one pass
   * per tile — thirteen full scans on every tap was already the slowest thing on
   * this screen before the Catalog tripled in size.
   */
  const counts = useMemo(() => {
    const byGenre = new Map<Genre, number>();
    const byDecade = new Map<Decade, number>();
    for (const track of tracks) {
      byDecade.set(track.decade, (byDecade.get(track.decade) ?? 0) + 1);
      for (const genre of track.genres) byGenre.set(genre, (byGenre.get(genre) ?? 0) + 1);
    }
    return { byGenre, byDecade };
  }, [tracks]);

  return (
    <div className="custom">
      <fieldset className="picks">
        <legend className="picks__legend">Genres</legend>
        <div className="picks__grid">
          <Tile
            on={filter.genres.length === 0}
            onClick={() => onChange({ ...filter, genres: [] })}
            glyph="🎵"
            hue={280}
            label="All"
            count={tracks.length}
          />
          {GENRES.map((g) => (
            <Tile
              key={g}
              on={filter.genres.includes(g)}
              onClick={() => onChange({ ...filter, genres: toggle(filter.genres, g) })}
              glyph={GENRE_VISUALS[g].glyph}
              hue={GENRE_VISUALS[g].hue}
              label={genreLabel(g)}
              count={counts.byGenre.get(g) ?? 0}
            />
          ))}
        </div>
      </fieldset>

      <fieldset className="picks">
        <legend className="picks__legend">Eras</legend>
        <div className="picks__grid picks__grid--eras">
          <Tile
            on={filter.decades.length === 0}
            onClick={() => onChange({ ...filter, decades: [] })}
            glyph="🕰️"
            hue={280}
            label="All"
            count={tracks.length}
          />
          {DECADES.map((d) => (
            <Tile
              key={d}
              on={filter.decades.includes(d)}
              onClick={() => onChange({ ...filter, decades: toggle(filter.decades, d) })}
              glyph="●"
              hue={DECADE_HUES[d]}
              label={decadeLabel(d)}
              count={counts.byDecade.get(d) ?? 0}
            />
          ))}
        </div>
      </fieldset>

      <p className="custom__count" role="status">
        {playable
          ? `${size} songs match`
          : `Only ${size} ${size === 1 ? "song matches" : "songs match"} — pick another era or genre.`}
      </p>

      <button className="btn btn--primary" type="button" disabled={!playable} onClick={onStart}>
        Start
      </button>
    </div>
  );
}

function toggle<T>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}

function Tile({
  on,
  onClick,
  glyph,
  hue,
  label,
  count,
}: {
  on: boolean;
  onClick: () => void;
  glyph: string;
  hue: number;
  label: string;
  count: number;
}) {
  return (
    <button
      type="button"
      className={`tile${on ? " tile--on" : ""}`}
      onClick={onClick}
      aria-pressed={on}
      style={{ "--tile-hue": hue } as React.CSSProperties}
    >
      <span className="tile__glyph" aria-hidden="true">
        {glyph}
      </span>
      <span className="tile__label">{label}</span>
      <span className="tile__count">{count}</span>
    </button>
  );
}

export { decadeLabel, genreLabel } from "./labels.ts";

export function Chip({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button type="button" className={`chip${on ? " chip--on" : ""}`} onClick={onClick} aria-pressed={on}>
      {children}
    </button>
  );
}
